import React, { useState } from 'react';
import { ReactComponent as ArrowDownFillIcon } from '../../../assets/icons/arrow-down-s-fill.svg';
import { ReactComponent as ArrowUpFillIcon } from '../../../assets/icons/arrow-up-s-fill.svg';
import { ReactComponent as ChevronDownIcon } from '../../../assets/icons/arrow-down-s-line.svg';
import { ReactComponent as FilterIcon } from '../../../assets/icons/filter.svg';
import '../../../Components/Common/typography.css';
import { formatLocaleNumber, formatYears } from '../../../helpers/number';
import { formatDirectorName } from '../../../helpers/string';
import { CompanyDirector } from '../../../types/companydirector.type';
import { CommonTooltipType } from '../../../types/common.type';
import { DirectorOrderBy, DirectorSortBy } from '../../../utils/type';
import SortDropdown, { SortOption } from './SortDropdown';

const MOBILE_OPTIONS: SortOption[] = [
  { label: 'Current officer name', value: DirectorSortBy.LastName },
  { label: 'Current position', value: DirectorSortBy.Title },
  { label: 'Officer/director (YRS)', value: DirectorSortBy.OldPosition },
  { label: 'In position (YRS)', value: DirectorSortBy.LatestPosition },
  { label: 'Total compensation (AUD)', value: DirectorSortBy.Compensation },
];

interface TableMobileProps {
  directors: CompanyDirector[];
  sortBy: DirectorSortBy;
  orderBy: DirectorOrderBy;
  onSortChange: (sortBy: DirectorSortBy, orderBy: DirectorOrderBy) => void;
  setTooltip: React.Dispatch<React.SetStateAction<CommonTooltipType>>;
}

const TableMobile: React.FC<TableMobileProps> = ({
  directors,
  sortBy,
  orderBy,
  onSortChange,
  setTooltip,
}) => {
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const handleTitleMouseEnter = (
    e: React.MouseEvent<HTMLElement>,
    content: string,
  ) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltip({
      content,
      show: true,
      x: rect.left + window.scrollX,
      y: rect.bottom + 2 + window.scrollY,
    });
  };
  const handleTitleMouseLeave = () =>
    setTooltip((prev) => ({ ...prev, show: false }));

  const handleRowClick = (id: number) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const handleSortSelect = (col: DirectorSortBy) => {
    onSortChange(
      col,
      sortBy === col && orderBy === DirectorOrderBy.Asc
        ? DirectorOrderBy.Desc
        : DirectorOrderBy.Asc,
    );
  };

  const toggleOrderBy = () => {
    onSortChange(
      sortBy,
      orderBy === DirectorOrderBy.Asc
        ? DirectorOrderBy.Desc
        : DirectorOrderBy.Asc,
    );
  };

  return (
    <div>
      <div className="director-mobile-header">
        <div
          className="director-th header-6"
          style={{ flex: 1, position: 'relative', cursor: 'pointer' }}
          onClick={toggleOrderBy}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && toggleOrderBy()}
        >
          <span>Current officer / Current position</span>
          {orderBy === DirectorOrderBy.Asc
            ? <ArrowUpFillIcon className="director-sort-icon director-sort-icon--fill" />
            : <ArrowDownFillIcon className="director-sort-icon director-sort-icon--fill" />
          }
          <FilterIcon
            className="director-filter-icon"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              setDropdownOpen((p) => !p);
            }}
            style={{ cursor: 'pointer' }}
          />
          <SortDropdown
            isOpen={dropdownOpen}
            currentSortBy={sortBy}
            options={MOBILE_OPTIONS}
            onSelect={handleSortSelect}
            onClose={() => setDropdownOpen(false)}
          />
        </div>
      </div>

      {directors.map((d) => {
        const isExpanded = expandedId === d.Officer_ID;
        return (
          <div
            key={d.Officer_ID}
            className="director-mobile-row"
            onClick={() => handleRowClick(d.Officer_ID)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && handleRowClick(d.Officer_ID)}
          >
            <div
              className={`director-td director-td--top ${isExpanded ? 'director-td-active' : ''}`}
            >
              <div className="director-name-group">
                <span
                  className={`director-name-text text-primary ${isExpanded ? 'body-m-bold' : 'body-m-regular'}`}
                >
                  {formatDirectorName(
                    d.NamePrefix,
                    d.FirstName,
                    d.MiddleName,
                    d.LastName,
                  )}
                </span>
                <span
                  className="director-position-text text-primary body-s-regular"
                  onMouseEnter={(e) =>
                    handleTitleMouseEnter(e, d.Latest_Long_Title)
                  }
                  onMouseLeave={handleTitleMouseLeave}
                >
                  {d.Latest_Long_Title}
                </span>
              </div>
            </div>
            <div className="director-td director-td--top">
              <ChevronDownIcon
                className={`director-chevron${isExpanded ? ' director-chevron--up' : ''}`}
              />
            </div>

            <div
              className={`director-mobile-details${isExpanded ? ' director-mobile-details--open' : ''}`}
            >
              <div className="director-mobile-detail-item">
                <span className="director-mobile-detail-label body-m-regular">
                  Officer/Director (Yrs)
                </span>
                <span className="director-mobile-detail-value body-m-regular">
                  {formatYears(d.Oldest_position_year)}
                </span>
              </div>
              <div className="director-mobile-detail-item">
                <span className="director-mobile-detail-label body-m-regular">
                  In Position (Yrs)
                </span>
                <span className="director-mobile-detail-value body-m-regular">
                  {formatYears(d.Latest_position_year)}
                </span>
              </div>
              <div className="director-mobile-detail-item">
                <span className="director-mobile-detail-label body-m-regular">
                  Total Compensation
                </span>
                <span className="director-mobile-detail-value body-m-regular">
                  {formatLocaleNumber(d.Latest_FYT_val)}
                </span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default TableMobile;
